import React from "react";
import { Button } from "react-bootstrap";

/**
 * Banner shown above the history view while the memory doc is STALE or
 * INDEXING, and when the hook has stopped waiting for one that never settled.
 *
 * The `gaveUp` state exists because STALE and INDEXING are only usually
 * transient: a worker that is down, or a job that dies mid-build, leaves the
 * document in one of them indefinitely. The hook stops polling after five
 * minutes without progress rather than pulling on a dead state for as long as
 * the tab is open, so the banner has to stop claiming that something is
 * happening, and offer a way to look again instead.
 *
 * `onRetry` must be the hook's checkAgain (look again, resume polling), never
 * refresh: refresh queues a NEW build job on every click.
 *
 * `progress` is the /status payload's buildProgress ({ summarised, total })
 * while the worker is summarising notes, so a long first build reads as a
 * moving count instead of an open-ended spinner.
 */
export default function StaleBanner({ status, gaveUp = false, onRetry, progress = null }) {
  if (gaveUp) {
    return (
      <div
        className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center"
        role="status"
        style={{ fontSize: "0.85rem" }}
      >
        <i className="fa-solid fa-clock me-2" style={{ fontSize: "0.85rem" }} />
        <span className="flex-grow-1">
          This history is still rebuilding in the background. It is taking
          longer than usual.
        </span>
        {onRetry && (
          <Button variant="outline-secondary" size="sm" onClick={onRetry}>
            Check again
          </Button>
        )}
      </div>
    );
  }

  const isBuilding = status === "INDEXING";
  const hasProgress = isBuilding && progress && progress.total > 0;
  const message = hasProgress
    ? `Building patient history: ${Math.min(progress.summarised, progress.total)} of ${progress.total} notes summarised…`
    : isBuilding
      ? "Building patient history. This may take a minute…"
      : "This history may not include the latest note. Refreshing in the background.";
  return (
    <div
      className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center"
      role="status"
      style={{ fontSize: "0.85rem" }}
    >
      <i className="fa-solid fa-rotate me-2 fa-spin" style={{ fontSize: "0.85rem" }} />
      <span>{message}</span>
    </div>
  );
}
